import { Button } from "../Button/index.js";
import { SearchBar } from "../SearchBar/index.js";
import { filterGroups, filterItems, findItemByValue, isGroupItems, selectItemsToDict, selectTriggerChevronClassName, selectTriggerLeftIconChildClassName } from "./Select.lib.js";
import { GroupsList } from "./local-components/GroupsList.js";
import { List } from "./local-components/List.js";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { IconArrowDown, IconLoader } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Select } from "@base-ui/react/select";
//#region src/Select/Select.tsx
const getPortalContainer = () => typeof document !== "undefined" ? document.body : void 0;
const SelectComponent = ({ className, onChange: handleChange, value, items: initialItems, placeholder, name, iconLeft, label, disabled, error, required, searchDelay = 350, serverSearch, size = "lg", isSearch = true, contentProps, isLoading, onClear: handleClear, visibleItemsCount, minVisibleItemsCount, clearButtonText = "Сбросить", portalInHead = true, fetchItemTooltip, defaultGroupsCollapsed = true, onBlur, selectedItemLabel }) => {
	const [search, setSearch] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const firstRenderRef = useRef(false);
	const isOpenRef = useRef(false);
	const triggerRef = useRef(null);
	const contentWidth = triggerRef.current?.offsetWidth && `${triggerRef.current?.offsetWidth}px`;
	const inputRef = useRef(null);
	const valueStr = typeof value === "string" ? value : "";
	const hasValue = valueStr.length > 0;
	const { data: itemsData, hasNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
		enabled: Boolean(serverSearch),
		queryKey: [
			"select",
			name,
			search
		],
		queryFn: ({ pageParam, signal }) => {
			if (!serverSearch) return [];
			return serverSearch.fetchItems(search, pageParam, signal);
		},
		getNextPageParam: serverSearch?.getNextPageParam ?? (() => void 0),
		initialPageParam: 1,
		gcTime: Infinity,
		staleTime: Infinity
	});
	const items = useMemo(() => (itemsData ? itemsData?.pages.flat() : initialItems) ?? [], [itemsData, initialItems]);
	const itemsDict = useMemo(() => {
		if (!items || items.length === 0) return {};
		return selectItemsToDict(items);
	}, [items]);
	const filteredItems = useMemo(() => {
		if (!search || !items || !isSearch || items.length === 0 || Boolean(serverSearch)) return items;
		return isGroupItems(items) ? filterGroups(search, items) : filterItems(search, items);
	}, [
		search,
		items,
		isSearch,
		serverSearch
	]);
	const handleSearchChange = (nextSearch) => {
		setSearch(nextSearch);
	};
	const notifyBlur = () => {
		onBlur?.(new FocusEvent("blur"));
	};
	const handleOpenChange = (open) => {
		isOpenRef.current = open;
		if (open) setSearch("");
		else notifyBlur();
		setIsOpen(open);
	};
	const handleTriggerBlur = (ev) => {
		if (isOpenRef.current) return;
		const nativeEvent = ev.nativeEvent;
		queueMicrotask(() => {
			if (!isOpenRef.current) onBlur?.(nativeEvent);
		});
	};
	useEffect(() => {
		if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
	}, [isOpen]);
	return /* @__PURE__ */ jsxs(Select.Root, {
		value: valueStr,
		onValueChange: (nextValue) => {
			if (typeof nextValue === "string" && (nextValue || firstRenderRef.current)) {
				const selectedItem = items ? findItemByValue(items, nextValue) : void 0;
				handleChange?.(nextValue, selectedItem);
			}
			if (!firstRenderRef.current) firstRenderRef.current = true;
		},
		name,
		disabled,
		open: isOpen,
		onOpenChange: handleOpenChange,
		children: [/* @__PURE__ */ jsxs("div", {
			className: cn("max-w-full", className),
			children: [
				label && /* @__PURE__ */ jsxs("p", {
					className: "paragraph-sm text-type-common pb-2",
					children: [label, required && /* @__PURE__ */ jsx("span", {
						className: "text-type-error",
						children: "\xA0*"
					})]
				}),
				/* @__PURE__ */ jsx(Trigger, {
					className: "group",
					size,
					isFilled: hasValue,
					isOpen,
					withError: Boolean(error),
					name,
					disabled,
					ref: triggerRef,
					onBlur: handleTriggerBlur,
					children: /* @__PURE__ */ jsxs("span", {
						className: "flex w-full max-w-full flex-nowrap items-center gap-3",
						children: [
							iconLeft && /* @__PURE__ */ jsx("span", {
								className: cn("[&>*]:(text-neutrals-60 transition-colors) [&>*]:group-hover:text-neutrals-60 [&[data-value='true']_&>*]:(text-neutrals-40) block shrink-0", selectTriggerLeftIconChildClassName(size)),
								"data-value": hasValue,
								children: iconLeft
							}),
							/* @__PURE__ */ jsx("span", {
								className: cn("text-type-light group-hover:text-type-minor group-focus:text-type-minor data-[filled=true]:text-type-common flex-1 truncate whitespace-nowrap text-left transition-colors", size === "xs" || size === "sm" ? "subtitle-sm" : "paragraph-base"),
								"data-filled": hasValue,
								children: selectedItemLabel || itemsDict[valueStr] || (hasValue ? serverSearch?.placeholderLabel || valueStr : "") || placeholder
							}),
							/* @__PURE__ */ jsx(Select.Icon, {
								className: "justify-self-end",
								children: /* @__PURE__ */ jsx(IconArrowDown, { className: cn(selectTriggerChevronClassName(size), "text-neutrals-60") })
							})
						]
					})
				}),
				error ? /* @__PURE__ */ jsx("p", {
					className: "text-system-error-50 mt-0.5",
					children: error
				}) : null
			]
		}), /* @__PURE__ */ jsx(Select.Portal, {
			container: getPortalContainer(),
			children: /* @__PURE__ */ jsx(Select.Positioner, {
				alignItemWithTrigger: false,
				side: "bottom",
				sideOffset: 5,
				positionMethod: portalInHead ? "absolute" : "fixed",
				className: cn("right-0 z-[9999] w-full", contentProps?.className),
				...contentProps,
				style: {
					width: contentWidth,
					...contentProps?.style
				},
				children: /* @__PURE__ */ jsxs(Select.Popup, {
					autoFocus: false,
					onKeyDownCapture: (e) => e.stopPropagation(),
					className: "bg-neutrals-white rounded-[0.875rem] py-1 shadow",
					children: [
						isSearch && /* @__PURE__ */ jsx("div", {
							className: "relative px-2.5 py-1",
							children: /* @__PURE__ */ jsx(SearchBar, {
								className: "relative z-50",
								value: search,
								onChange: handleSearchChange,
								size: "sm",
								debounceTime: searchDelay,
								clearable: true,
								onClear: () => handleSearchChange(""),
								ref: inputRef
							})
						}),
						!isLoading && filteredItems?.length > 0 && !isGroupItems(filteredItems) && /* @__PURE__ */ jsx(Select.List, { children: /* @__PURE__ */ jsx(List, {
							items: filteredItems,
							search: isSearch ? search : "",
							hasNextPage,
							isFetching,
							onScrollEnd: fetchNextPage,
							visibleItemsCount: visibleItemsCount ?? 8,
							minVisibleItemsCount,
							fetchItemTooltip
						}) }),
						!isLoading && filteredItems?.length > 0 && isGroupItems(filteredItems) && /* @__PURE__ */ jsx(Select.List, { children: /* @__PURE__ */ jsx(GroupsList, {
							groups: filteredItems,
							search: isSearch ? search : "",
							hasNextPage,
							isFetching,
							onScrollEnd: fetchNextPage,
							defaultGroupsCollapsed,
							visibleItemsCount: visibleItemsCount ?? 8,
							minVisibleItemsCount
						}) }),
						(isLoading || isFetching && !itemsData) && /* @__PURE__ */ jsx("div", {
							className: "flex items-center justify-center py-4",
							children: /* @__PURE__ */ jsx(IconLoader, { className: "text-neutrals-60 size-6 animate-spin" })
						}),
						!isLoading && !isFetching && serverSearch && search && items.length === 0 && /* @__PURE__ */ jsxs("div", {
							className: "paragraph-sm text-type-light px-4 py-3 text-center",
							children: [
								"По запросу «",
								search,
								"» ничего не найдено"
							]
						}),
						handleClear && /* @__PURE__ */ jsx("div", {
							className: "px-2.5 pb-2 drop-shadow",
							children: /* @__PURE__ */ jsx(Button, {
								onClick: () => {
									handleClear();
									setIsOpen(false);
								},
								className: "w-full",
								theme: "text",
								size: "sm",
								disabled: !hasValue,
								children: clearButtonText || "Сбросить"
							})
						})
					]
				})
			})
		})]
	});
};
const Trigger = forwardRef((props, ref) => {
	const { size, isFilled, isOpen, withError, className, ...restProps } = props;
	const sizeClass = size === "lg" && "p-2.5" || size === "md" && "p-1.5" || size === "xs" && "px-[0.1875rem] py-px" || "h-8 rounded-[0.625rem] p-[0.1875rem]";
	return /* @__PURE__ */ jsx(Select.Trigger, {
		ref,
		className: cn("w-full", "bg-neutrals-95 hover:bg-neutrals-90 hover:outline-system-info-70 focus:outline-system-info-70 disabled:!bg-neutrals-80 m-[0.0625rem] flex flex-nowrap items-center gap-4 rounded-[0.875rem] border-2 border-solid border-transparent p-2.5 outline outline-2 outline-offset-1 outline-transparent disabled:cursor-not-allowed", sizeClass, isFilled && "border-neutrals-90", isOpen && "border-neutrals-80 bg-neutrals-90", withError && "!outline-system-error-50 hover:!outline-system-error-50 focus:!outline-system-error-50 focus-visible:!outline-system-error-50 focus-visible:shadow-[0_0_0_4px_rgba(255,84,73,0.5)]", className),
		...restProps
	});
});
Trigger.displayName = "SelectTrigger";
//#endregion
export { SelectComponent };

//# sourceMappingURL=Select.js.map